import { Box, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useRef } from 'react';

import { SelectionListControls } from '../../../../selection-lists/components/SelectionListControls/SelectionListControls';
import type { TNode } from '../../Node';
import { useTreeSelections } from '../../use-tree-selections';
import type { TreeApi, TreeData } from '../../useTreeApi';
import type { PopoutTreeProps } from '../PopoutTree';
import type { BasePopoutTreeCommonProps } from '../types';
import { AsyncCompanySearchTree } from './AsyncCompanySearchTree';
import { SelectedValuesDisplayComponent } from './SelectedValueDisplay';

export interface AllCompaniesPopoutTreeProps extends BasePopoutTreeCommonProps {
  treeData: TreeData[];
  activeIds: string[];
  activeNodeData?: TreeData[];
  maxTopLevelSelections?: number;
  searchInputRef?: PopoutTreeProps['searchInputRef'];
  onSubmit: (selectedItems: string[], selectedNodeData: TNode[]) => void;
  isSelfSignupUser?: boolean;
  selectionChange?: () => void;
  middleContent?: React.ReactNode;
  bottomContent?: React.ReactNode;
  showSelectedValues?: boolean;
  replaceSingleSelections?: boolean;
  isSelectionListIdsEnabled?: boolean;
}

export function AllCompaniesPopoutTree({
  treeData,
  activeIds,
  activeNodeData = [],
  filterLabel,
  minSelections = 0,
  maxSelections,
  maxTopLevelSelections,
  branches,
  disabledLevels,
  disallowMultiLevelSelect = false,
  sortSuggestedSearch = false,
  isOpen,
  searchInputRef,
  onSubmit,
  onClose,
  isSelfSignupUser,
  selectionChange,
  middleContent,
  bottomContent,
  showSelectedValues = false,
  replaceSingleSelections = false,
  isSelectionListIdsEnabled,
}: AllCompaniesPopoutTreeProps) {
  const {
    selections,
    setSelections,
    onChange,
    onClear,
    validateMinSelections,
    selectedNodeData,
  } = useTreeSelections({
    initialSelections: activeIds,
    disallowMultiLevelSelect,
    maxSelections,
    minSelections,
    filterLabel,
    maxTopLevelSelections,
    replaceSingleSelections,
  });

  const previousSelectionsRef = useRef<string[]>(activeIds);

  useEffect(() => {
    if (selectionChange) {
      const selectionsChanged =
        selections.length !== previousSelectionsRef.current.length ||
        selections.some(
          (id, index) => id !== previousSelectionsRef.current[index]
        );

      if (selectionsChanged) {
        selectionChange();
        previousSelectionsRef.current = [...selections];
      }
    }
  }, [selections, selectionChange]);

  useEffect(() => {
    setSelections(activeIds);
    previousSelectionsRef.current = [...activeIds];
  }, [activeIds, setSelections]);

  const resetSelections = useCallback(() => {
    setSelections(activeIds);
  }, [activeIds, setSelections]);

  const popoutTreeRef = useRef<
    TreeApi & { onResetSearch: () => void; scrollToTop: () => void }
  >();

  useEffect(() => {
    const treeApi = popoutTreeRef.current;
    if (!treeApi) {
      // Tree component might not be mounted yet
      return;
    }

    if (isOpen) {
      resetSelections(); // Reset selections based on activeIds
      previousSelectionsRef.current = [...activeIds]; // Sync ref with reset state
      treeApi.onSearch(''); // Trigger initial/empty search

      treeApi.scrollToTop?.();
      return;
    }

    treeApi.onResetSearch?.(); // Reset search state (clear input, cancel requests)

    // Dependencies: Run when isOpen changes or when resetSelections identity changes.
  }, [isOpen, resetSelections, activeIds]);

  const handleSubmit = () => {
    if (!validateMinSelections()) {
      return;
    }

    onSubmit(selections, selectedNodeData);

    if (onClose) {
      onClose();
    }
  };

  const activeNodeDataIdSet = new Set(activeNodeData.map((node) => node.id));
  const selectedNodeTreeData = selectedNodeData.map((node) => node.data);
  const filteredSelectedNodeTreeData = selectedNodeTreeData.filter(
    (node) => !activeNodeDataIdSet.has(node.id)
  );

  const allNodeData = [...activeNodeData, ...filteredSelectedNodeTreeData];

  // Get selected TreeData for display
  const selectedTreeData = allNodeData?.filter((node) =>
    selections.includes(node.id)
  );

  // Handle selection removal from SelectedValuesDisplay
  const handleRemoveSelection = (selectionId: string) => {
    const updatedSelections = selections.filter((id) => id !== selectionId);
    setSelections(updatedSelections);
  };

  if (!treeData || treeData.length === 0) {
    return null;
  }

  return (
    <VStack
      spacing={4}
      align="stretch"
      data-testid="all-companies-popout-tree-container"
    >
      {showSelectedValues && selectedTreeData.length > 0 && (
        <SelectedValuesDisplayComponent
          selections={selectedTreeData}
          onRemove={handleRemoveSelection}
        />
      )}
      <VStack spacing={2} align="stretch">
        <AsyncCompanySearchTree
          data={treeData}
          value={selections}
          disabledLevels={disabledLevels}
          onChange={onChange}
          branches={branches}
          sortSuggestedSearch={sortSuggestedSearch}
          searchInputRef={searchInputRef}
          ref={popoutTreeRef}
          selectedNodeData={selectedNodeData}
          isOpen={isOpen}
          middleContent={middleContent}
          isSelectionListIdsEnabled={isSelectionListIdsEnabled}
        />
      </VStack>
      {bottomContent && <Box>{bottomContent}</Box>}
      <SelectionListControls
        onClear={onClear}
        onClose={onClose}
        onSubmit={handleSubmit}
        isSubmitDisabled={selections.length === 0 || isSelfSignupUser}
        submitText={activeIds.length ? 'Update' : 'Add'}
        tooltipText={
          isSelfSignupUser
            ? 'Upgrade required'
            : 'Please make the minimum required selections'
        }
      />
    </VStack>
  );
}
